// logger.js - Centralized logging service for the Pista mobile app
import { authService } from '../services/auth';

// Log levels in order of severity
const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  SILENT: 4,
};

// Environment-based configuration
const getLogLevel = () => {
  const env = process.env.NODE_ENV || process.env.REACT_APP_ENV || 'development';

  switch (env) {
    case 'production':
      return LOG_LEVELS.ERROR; // Only errors in production
    case 'test':
      return LOG_LEVELS.SILENT; // No logs in tests
    case 'staging':
      return LOG_LEVELS.WARN; // Warnings and errors in staging
    default:
      return LOG_LEVELS.DEBUG; // All logs in development
  }
};

// Remote logging configuration
const REMOTE_LOGGING_ENABLED = process.env.NODE_ENV === 'production' ||
                                process.env.REACT_APP_ENV === 'staging';

class Logger {
  constructor() {
    this.currentLogLevel = getLogLevel();
    this.userContext = null;
    this.sessionId = this.generateSessionId();
  }

  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  setUserContext(user) {
    this.userContext = user ? {
      id: user.id,
      email: user.email,
      username: user.username,
    } : null;
  }

  shouldLog(level) {
    return level >= this.currentLogLevel;
  }

  formatMessage(level, message, context = {}) {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      level: Object.keys(LOG_LEVELS)[level],
      message,
      context: {
        ...context,
        sessionId: this.sessionId,
        userAgent: navigator.userAgent,
        url: window.location.href,
        ...(this.userContext && { user: this.userContext }),
      },
    };

    return logEntry;
  }

  async logToRemote(logEntry) {
    if (!REMOTE_LOGGING_ENABLED) return;

    try {
      // Only send ERROR and WARN logs to remote service
      if (logEntry.level === 'ERROR' || logEntry.level === 'WARN') {
        await fetch(`${process.env.REACT_APP_API_BASE || ''}/logs`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(authService.getAuthHeaders?.() || {}),
          },
          body: JSON.stringify(logEntry),
        }).catch(() => {
          // Silently fail remote logging to avoid infinite loops
        });
      }
    } catch (error) {
      // Silently fail remote logging
    }
  }

  log(level, message, context = {}) {
    if (!this.shouldLog(level)) return;

    const logEntry = this.formatMessage(level, message, context);

    // Console output with appropriate styling
    const consoleMethod = this.getConsoleMethod(level);
    const prefix = `[${logEntry.timestamp}] ${logEntry.level}:`;

    if (logEntry.context && Object.keys(logEntry.context).length > 0) {
      consoleMethod(prefix, message, logEntry.context);
    } else {
      consoleMethod(prefix, message);
    }

    // Remote logging for production
    this.logToRemote(logEntry);
  }

  getConsoleMethod(level) {
    switch (level) {
      case LOG_LEVELS.DEBUG:
        // eslint-disable-next-line no-console
        return console.debug;
      case LOG_LEVELS.INFO:
        // eslint-disable-next-line no-console
        return console.info;
      case LOG_LEVELS.WARN:
        return console.warn;
      case LOG_LEVELS.ERROR:
        return console.error;
      default:
        // eslint-disable-next-line no-console
        return console.log;
    }
  }

  // Convenience methods
  debug(message, context = {}) {
    this.log(LOG_LEVELS.DEBUG, message, context);
  }

  info(message, context = {}) {
    this.log(LOG_LEVELS.INFO, message, context);
  }

  warn(message, context = {}) {
    this.log(LOG_LEVELS.WARN, message, context);
  }

  error(message, context = {}) {
    this.log(LOG_LEVELS.ERROR, message, context);
  }

  // Specialized logging methods
  api(method, url, status, duration, context = {}) {
    this.info(`API ${method} ${url}`, {
      status,
      duration: `${duration}ms`,
      ...context,
    });
  }

  userAction(action, details = {}) {
    this.debug(`User action: ${action}`, {
      action,
      ...details,
    });
  }

  performance(metric, value, context = {}) {
    this.info(`Performance: ${metric}`, {
      metric,
      value,
      ...context,
    });
  }

  component(componentName, lifecycle, context = {}) {
    this.debug(`Component ${componentName}: ${lifecycle}`, {
      component: componentName,
      lifecycle,
      ...context,
    });
  }
}

// Create singleton instance
const logger = new Logger();

// Export the logger instance and log levels
export default logger;
export { LOG_LEVELS };
